import { useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getListAuditV1AuditGetQueryKey,
  getListUsersV1UsersGetQueryKey,
  useDeleteUserV1UsersUserIdDelete,
  useInviteUserV1UsersPost,
  useListAuditV1AuditGet,
  useListUsersV1UsersGet,
  useUpdateUserV1UsersUserIdPatch,
} from "../api/generated/qcsControlPlane";
import type { AuditOut, Role, UserOut, UserStatus } from "../api/generated/model";
import { parseTs } from "../api/labels";
import { type AnyColumnDef, DataTable, createColumnHelper } from "../components/DataTable";
import { initials } from "../components/Shell";
import { IconPlus } from "../components/icons";
import { useToast } from "../components/toast";
import { useCurrentUser } from "../auth/me";
import { Badge, Button, Dot, Input, PageHeader, Panel, Row, Stack, Tag, type DotTone as Tone } from "@akaike/ui";
import { toneVariant } from "../lib/badge-tone";
import { Field, Stat } from "../components/foundation";
import { AnimatedNumber } from "../lib/motion";

type Audit = AuditOut;

// The customFetch mutator throws Error(message) with FastAPI's detail already
// unwrapped, so react-query hands us a plain Error.
function errMsg(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

const fmtTime = (iso: string) =>
  parseTs(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

export function Users() {
  const me = useCurrentUser();
  const qc = useQueryClient();
  const notify = useToast();
  const emailRef = useRef<HTMLInputElement>(null);

  const usersQuery = useListUsersV1UsersGet();
  const auditQuery = useListAuditV1AuditGet();

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: getListUsersV1UsersGetQueryKey() });
    qc.invalidateQueries({ queryKey: getListAuditV1AuditGetQueryKey() });
  };

  const patch = useUpdateUserV1UsersUserIdPatch({
    mutation: {
      onSuccess: () => {
        notify("User updated");
        invalidate();
      },
      onError: (error) => notify(errMsg(error, "Update failed"), "crit"),
    },
  });

  const remove = useDeleteUserV1UsersUserIdDelete({
    mutation: {
      onSuccess: () => {
        notify("User removed");
        invalidate();
      },
      onError: (error) => notify(errMsg(error, "Delete failed"), "crit"),
    },
  });

  const rows = usersQuery.data ?? [];
  const superusers = rows.filter((u) => u.role === "superuser").length;
  const members = rows.filter((u) => u.role === "member").length;
  const viewers = rows.filter((u) => u.role === "viewer").length;
  const invited = rows.filter((u) => u.status === "invited").length;
  const busy = patch.isPending || remove.isPending;

  const columns = useMemo<AnyColumnDef<UserOut>[]>(() => {
    const col = createColumnHelper<UserOut>();
    return [
      col.accessor("name", {
        id: "user",
        header: "User",
        cell: ({ row }) => {
          const u = row.original;
          const isInvited = u.status === "invited";
          const isDisabled = u.status === "disabled";
          const avClass = isInvited ? "invited" : isDisabled ? "disabled" : `av-${row.index % 5}`;
          return (
            <Row gap="sm">
              <div className={`avatar ${avClass}`}>{initials(u.name, u.email)}</div>
              {u.id === me.id && (
                <Tag style={{ fontSize: 10, padding: "1px 5px" }}>You</Tag>
              )}
            </Row>
          );
        },
        meta: {
          cellClassName: "strong",
          minWidth: 150,
          exportValue: (u) => u.name ?? u.email,
        },
      }),
      col.accessor("email", {
        header: "Email",
        cell: ({ row }) => (
          <span className="mono" style={{ fontSize: 11.5, color: "var(--ink-2)" }}>{row.original.email}</span>
        ),
        meta: { minWidth: 200 },
      }),
      col.accessor("role", {
        header: "Role",
        cell: ({ row }) => (
          <Badge variant={toneVariant(row.original.role === "superuser" ? "brand" : "info")}>{row.original.role}</Badge>
        ),
        meta: { width: 130, filterSelect: true, filterLabel: "Role" },
      }),
      col.accessor("status", {
        header: "Status",
        cell: ({ row }) => <StatusPill status={row.original.status} />,
        meta: { width: 130, filterSelect: true, filterLabel: "Status" },
      }),
      col.accessor("last_login_at", {
        header: "Last login",
        cell: ({ row }) =>
          row.original.last_login_at ? (
            <span className="num" style={{ fontSize: 11.5 }}>{fmtTime(row.original.last_login_at)}</span>
          ) : (
            <span className="faint" style={{ fontSize: 11.5 }}>—</span>
          ),
        meta: { width: 150, exportValue: (u) => u.last_login_at },
      }),
      col.display({
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const u = row.original;
          // Operators cannot act on their own account.
          if (u.id === me.id) return <span className="faint" style={{ fontSize: 11 }}>—</span>;
          const isSuper = u.role === "superuser";
          const isViewer = u.role === "viewer";
          const isDisabled = u.status === "disabled";
          const isInvited = u.status === "invited";
          return (
            <Row gap="xs">
              {/* A viewer steps up to member first; never one click to full admin. */}
              {!isViewer && (
                <Button
                  size="sm"
                  disabled={busy}
                  onClick={() =>
                    patch.mutate({
                      userId: u.id,
                      data: { role: isSuper ? "member" : "superuser" },
                    })
                  }
                >
                  {isSuper ? "Demote" : "Promote"}
                </Button>
              )}
              {!isSuper && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() =>
                    patch.mutate({
                      userId: u.id,
                      data: { role: isViewer ? "member" : "viewer" },
                    })
                  }
                >
                  {isViewer ? "Make member" : "Make viewer"}
                </Button>
              )}
              {!isInvited &&
                (isDisabled ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => patch.mutate({ userId: u.id, data: { status: "active" } })}
                  >
                    Enable
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() =>
                      patch.mutate({
                        userId: u.id,
                        data: { status: "disabled" },
                      })
                    }
                  >
                    Disable
                  </Button>
                ))}
              <Button
                size="sm"
                variant="destructive"
                disabled={busy}
                onClick={() => remove.mutate({ userId: u.id })}
              >
                {isInvited ? "Revoke" : "Remove"}
              </Button>
            </Row>
          );
        },
        meta: { minWidth: 330 },
        enableSorting: false,
        enableHiding: false,
      }),
    ];
    // patch / remove are stable react-query handles; re-memo on identity + busy.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.id, busy]);

  return (
    <>
      <PageHeader
        className="mb-3.5"
        title="Users"
        description="Google SSO · invite-only · superuser-managed"
        action={
          <Button variant="primary" onClick={() => emailRef.current?.focus()}>
            <IconPlus />
            Invite user
          </Button>
        }
      />

      <div className="grid g-5 mb-3.5">
        <Stat surface label="Total users" value={<AnimatedNumber value={rows.length} />} foot={<span className="faint">across all roles</span>} />
        <Stat surface label="Superusers" value={<AnimatedNumber value={superusers} />} tone="brand" foot={<span className="faint">full admin access</span>} />
        <Stat surface label="Members" value={<AnimatedNumber value={members} />} foot={<span className="faint">read + trigger access</span>} />
        <Stat surface label="Viewers" value={<AnimatedNumber value={viewers} />} foot={<span className="faint">read-only access</span>} />
        <Stat surface label="Pending invites" value={<AnimatedNumber value={invited} />} tone={invited ? "warn" : undefined} foot={<span className="faint">awaiting first sign-in</span>} />
      </div>

      <div className="grid" style={{ gridTemplateColumns: "repeat(12,1fr)", alignItems: "start" }}>
        <Panel style={{ gridColumn: "span 8" }}>
          <Panel.Head title="Members">
            <div className="flex-1" />
            <span className="num faint text-[11px]">{rows.length} users</span>
          </Panel.Head>
          <Panel.Body tight>
            <DataTable
              tableKey="users"
              fillHeight
              columns={columns}
              data={rows}
              getRowId={(u) => u.id}
              globalPlaceholder="Search users…"
              csvFilename="users"
              rowClassName={(u) => (u.status === "disabled" ? "row-dim" : undefined)}
              emptyMessage="No users yet."
              isLoading={usersQuery.isLoading}
            />
          </Panel.Body>
        </Panel>

        <Stack gap="lg" style={{ gridColumn: "span 4" }}>
          <InvitePanel emailRef={emailRef} onInvited={invalidate} />
          <AuditPanel entries={auditQuery.data ?? []} />
        </Stack>
      </div>
    </>
  );
}

function StatusPill({ status }: { status: UserStatus }) {
  if (status === "active")
    return (
      <Badge variant="outline-success">
        <Dot tone="ok" /> active
      </Badge>
    );
  if (status === "invited")
    return (
      <Badge variant="outline-warning">
        <Dot tone="warn" /> invited
      </Badge>
    );
  return <Badge variant="outline">disabled</Badge>;
}

function InvitePanel({
  emailRef,
  onInvited,
}: {
  emailRef: React.RefObject<HTMLInputElement | null>;
  onInvited: () => void;
}) {
  const notify = useToast();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("member");

  const invite = useInviteUserV1UsersPost({
    mutation: {
      onSuccess: () => {
        notify(`Invited ${email}`);
        setEmail("");
        setRole("member");
        onInvited();
      },
      onError: (error) => notify(errMsg(error, "Invite failed"), "crit"),
    },
  });

  return (
    <Panel>
      <Panel.Head title="Invite user" />
      <Panel.Body>
        <Stack gap="lg">
          <Field label="Email address">
            <Input
              ref={emailRef}
              placeholder="name@akaiketech.com"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && email.trim()) invite.mutate({ data: { email, role } });
              }}
            />
          </Field>
          <Field label="Role">
            <div className="seg">
              <button className={role === "viewer" ? "on" : ""} onClick={() => setRole("viewer")}>
                viewer
              </button>
              <button className={role === "member" ? "on" : ""} onClick={() => setRole("member")}>
                member
              </button>
              <button
                className={role === "superuser" ? "on brand" : ""}
                onClick={() => setRole("superuser")}
              >
                superuser
              </button>
            </div>
          </Field>
          <p className="hint">
            They gain access on their next Google sign-in. Un-invited sign-ins are rejected, as are
            addresses outside @akaiketech.com that an admin has not allowed.
          </p>
          <Button
            variant="primary"
            style={{ width: "100%", justifyContent: "center" }}
            disabled={invite.isPending || email.trim().length === 0}
            onClick={() => invite.mutate({ data: { email, role } })}
          >
            {invite.isPending ? "Sending…" : "Send invite"}
          </Button>
        </Stack>
      </Panel.Body>
    </Panel>
  );
}

const AUDIT_DOT: Record<string, Tone> = {
  "user.invite": "warn",
  "user.update": "brand",
  "user.delete": "crit",
};

function auditText(a: Audit) {
  const target = a.target ?? "—";
  const role = (a.detail as { role?: string } | null)?.role;
  const status = (a.detail as { status?: string } | null)?.status;
  if (a.action === "user.invite") return <>invited <b>{target}</b> as {role ?? "member"}</>;
  if (a.action === "user.update")
    return (
      <>
        updated <b>{target}</b> → {[role, status].filter(Boolean).join(" / ") || "changed"}
      </>
    );
  if (a.action === "user.delete") return <>removed <b>{target}</b></>;
  return (
    <>
      {a.action} <b>{target}</b>
    </>
  );
}

function AuditPanel({ entries }: { entries: Audit[] }) {
  return (
    <Panel>
      <Panel.Head title="Audit log">
        <div className="flex-1" />
        <span className="faint" style={{ fontFamily: "var(--f-mono)", fontSize: 10 }}>
          recent
        </span>
      </Panel.Head>
      <Panel.Body className="feed">
        {entries.length === 0 && (
          <div className="muted" style={{ fontFamily: "var(--f-sans)" }}>
            No activity yet.
          </div>
        )}
        {entries.map((a) => (
          <div className="ln" key={a.id}>
            <Dot tone={AUDIT_DOT[a.action] ?? "info"} />
            <span>
              {auditText(a)}, <span style={{ color: "var(--ink-3)" }}>{a.actor_email}</span>
            </span>
            <time style={{ marginLeft: "auto" }}>{fmtTime(a.at)}</time>
          </div>
        ))}
      </Panel.Body>
    </Panel>
  );
}
